import React, { useRef, useState } from 'react';
import moment, { Moment } from 'moment';
import MaterialInput from '../MaterialInput';
import { DATE_FORMAT_PICKER } from 'src/config';
import styled from 'styled-components';
import { PickersCalendarHeader, PickersDay, PickersLayout } from '@mui/x-date-pickers';
import { ActionBar, Toolbar } from './comps';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import _ from 'lodash';
import { DatePicker } from '@mui/x-date-pickers';
import { Calendar } from 'src/assets/dynamicSvg';
import { useStateSelector } from 'src/redux';

const StyledPickersLayout = styled(PickersLayout)`
  .MuiDayCalendar-weekDayLabel {
    color: ${({ theme }) => theme.colors.cflowerBlue} !important;
  }
`;

const StyledHeader = styled(PickersCalendarHeader)`
  .MuiPickersCalendarHeader-label {
    color: ${({ theme }) => theme.colors.prussianBlue};
  }
  .MuiSvgIcon-root {
    color: ${({ theme }) => theme.colors.cflowerBlue};
  }
`;

const StyledDay = styled(PickersDay)`
  font-weight: 500 !important;

  &.MuiPickersDay-root {
    color: ${({ theme }) => theme.colors.prussianBlue};
  }
  &.Mui-selected {
    background: ${({ theme }) => theme.colors.gradient} !important;
  }

  .adornment {
    display: flex;
    &__value {
      width: 35px;
      &__btn {
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        top: 0;
        bottom: 0;
        background: ${({ theme }) => theme.colors.cultured};
      }
    }
  }

  &.MuiDateCalendar-root {
    border: 2px solid red !important;
  }
`;

interface InputProps extends React.ComponentProps<typeof MaterialInput> {
  ['data-cy']?: string;
}

interface MaterialDatePickerProps extends Omit<React.ComponentProps<typeof DatePicker>, 'value' | 'onChange'> {
  value: string | null | undefined;
  onChange: (date: string | null) => void;
  inputProps?: InputProps;
}

const MaterialDatePicker: React.FC<MaterialDatePickerProps> = ({ value, inputProps, onChange, ...rest }) => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  const [open, setOpen] = useState(false);
  const hadValueOnOpen = useRef(false);

  const { onClose: externalOnClose, onOpen: externalOnOpen, ...restProps } = rest as any;

  const handleOnDateChange = (date: Moment | null) => {
    onChange(date ? date.toISOString() : null);
  };

  const handlePeriodSelect = (date: Moment) => {
    handleOnDateChange(date);
    setOpen(false);
  };

  return (
    <DesktopDatePicker
      open={open}
      onOpen={() => {
        hadValueOnOpen.current = !!value;
        setOpen(true);
        externalOnOpen?.();
      }}
      onClose={() => {
        setOpen(false);
        externalOnClose?.();
      }}
      value={value ? moment(value) : null}
      onChange={handleOnDateChange}
      format={DATE_FORMAT_PICKER}
      slots={{
        textField: MaterialInput,
        toolbar: Toolbar,
        actionBar: ActionBar,
        day: StyledDay,
        calendarHeader: StyledHeader,
        openPickerIcon: Calendar,
        layout: StyledPickersLayout,
      }}
      slotProps={{
        textField: {
          ...inputProps,
        },
        actionBar: {
          onPeriodSelect: handlePeriodSelect,
        } as any,
        popper: {
          placement: 'auto',
          sx: {
            '.MuiPickersLayout-root': {
              background: colors.white,
            },
          },
        },
        openPickerButton: {
          sx: {
            marginRight: 1,
          },
        },
        toolbar: {
          toolbarFormat: DATE_FORMAT_PICKER,
          showToolbar: hadValueOnOpen.current,
        } as any,
      }}
      {...restProps}
    />
  );
};
export default MaterialDatePicker;
